import { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';

/**
 * Custom hook to manage admin controls and user selection
 * @param {Object} user - Current user object
 * @param {number} currentSeasonId - Current season ID
 * @returns {Object} Admin controls state and handlers
 */
export const useAdminControls = (user, currentSeasonId) => {
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isViewingAsAdmin, setIsViewingAsAdmin] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveReason, setSaveReason] = useState('');
  const [adminSaving, setAdminSaving] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';

  // Fetch users for admin user selection
  useEffect(() => {
    const fetchUsers = async () => {
      if (isAdmin && currentSeasonId) {
        try {
          const response = await adminAPI.getAllUsers(currentSeasonId);
          setAllUsers(response.data.users);
        } catch (err) {
          console.error('Failed to fetch users:', err);
        }
      }
    };
    fetchUsers();
  }, [isAdmin, currentSeasonId]);

  /**
   * Handle user selection change
   * @param {number} newUserId - New user ID
   */
  const handleUserSelect = (newUserId) => {
    setSelectedUserId(newUserId);
    setIsViewingAsAdmin(newUserId !== user.id);
  };

  /**
   * Return to viewing own picks
   */
  const returnToOwnPicks = () => {
    setSelectedUserId(user.id);
    setIsViewingAsAdmin(false);
  };

  /**
   * Open the batch save dialog
   */
  const openSaveDialog = () => {
    setShowSaveDialog(true);
  };

  /**
   * Close the batch save dialog and clear reason
   */
  const closeSaveDialog = () => {
    setShowSaveDialog(false);
    setSaveReason('');
  };

  /**
   * Batch save all changed picks for a user
   * @param {number} userId - Target user ID
   * @param {Object} picks - Current picks map { matchId: teamId }
   * @param {Object} originalPicks - Original picks map { matchId: teamId }
   * @param {Array} matches - Array of match objects
   * @param {string} reason - Edit reason
   * @returns {Promise<Object>} Result object
   */
  const batchSaveAdminPicks = async (userId, picks, originalPicks, matches, reason) => {
    setAdminSaving(true);
    try {
      // Compute changed picks
      const changedPicks = [];
      for (const [matchIdStr, teamId] of Object.entries(picks)) {
        const matchId = parseInt(matchIdStr, 10);
        if (originalPicks[matchId] !== teamId) {
          const match = matches.find((m) => m.id === matchId);
          const existingPickId = match?.pick?.id;
          changedPicks.push({ matchId, teamId, ...(existingPickId ? { existingPickId } : {}) });
        }
      }

      const response = await adminAPI.batchSavePicksForUser({
        userId,
        picks: changedPicks,
        editReason: reason,
      });

      return response.data;
    } finally {
      setAdminSaving(false);
    }
  };

  return {
    isAdmin,
    isSuperAdmin,
    allUsers,
    selectedUserId,
    isViewingAsAdmin,
    showSaveDialog,
    saveReason,
    adminSaving,
    setSaveReason,
    handleUserSelect,
    returnToOwnPicks,
    openSaveDialog,
    closeSaveDialog,
    batchSaveAdminPicks,
  };
};
